import * as React from "react";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Badge,
} from "@repo/ui";
import { X, Check, ChevronDown } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@repo/utils";
import { DateRangeFilter } from "@/components/forms";
import { getAuthUsers } from "@/lib/api/auth";
import type { AuditActionType } from "@/lib/types/inventory";

export interface AuditLogFiltersProps {
  onFilterChange: (filters: Record<string, string>) => void;
}

const actionTypes: { value: AuditActionType | "all"; label: string }[] = [
  { value: "all", label: "All Actions" },
  { value: "receipt_created", label: "Receipt Created" },
  { value: "receipt_edited", label: "Receipt Edited" },
  { value: "receipt_deleted", label: "Receipt Deleted" },
  { value: "dispense_created", label: "Dispense Created" },
  { value: "dispense_edited", label: "Dispense Edited" },
  { value: "dispense_deleted", label: "Dispense Deleted" },
  { value: "inventory_updated", label: "Inventory Updated" },
  { value: "stock_adjustment_created", label: "Adjustment Created" },
  { value: "stock_adjustment_edited", label: "Adjustment Edited" },
  { value: "stock_adjustment_deleted", label: "Adjustment Deleted" },
  { value: "stock_manual_override", label: "Stock Override" },
  { value: "stock_override_cleared", label: "Override Cleared" },
  { value: "threshold_changed", label: "Threshold Changed" },
  { value: "expiration_alert_changed", label: "Expiration Alert Changed" },
  { value: "initial_stock_adjusted", label: "Initial Stock Adjusted" },
  { value: "inventory_audit_created", label: "Audit Created" },
  { value: "inventory_audit_deleted", label: "Audit Deleted" },
  { value: "authorization_approved", label: "Authorization Approved" },
  { value: "authorization_denied", label: "Authorization Denied" },
  {
    value: "permission_override_changed",
    label: "Permission Override Changed",
  },
  { value: "two_factor_enabled", label: "2FA Enabled" },
  { value: "two_factor_disabled", label: "2FA Disabled" },
  { value: "two_factor_verified", label: "2FA Verified" },
  { value: "two_factor_recovery_used", label: "2FA Recovery Used" },
  {
    value: "two_factor_recovery_regenerated",
    label: "2FA Recovery Regenerated",
  },
  {
    value: "two_factor_enforcement_changed",
    label: "2FA Enforcement Changed",
  },
];

interface User {
  id: string;
  initials: string;
  full_name: string | null;
}

interface FilterState {
  startDate?: Date;
  endDate?: Date;
  actionType: string;
  tableName: string;
  userIds: string[];
}

export function AuditLogFilters({ onFilterChange }: AuditLogFiltersProps) {
  const [filters, setFilters] = React.useState<FilterState>({
    actionType: "all",
    tableName: "all",
    userIds: [],
  });
  const [users, setUsers] = React.useState<User[]>([]);
  const [usersLoading, setUsersLoading] = React.useState(true);
  const [usersOpen, setUsersOpen] = React.useState(false);

  React.useEffect(() => {
    async function fetchUsers() {
      try {
        const data = await getAuthUsers({ pageSize: 100 });
        setUsers(data.data?.users || data.users || []);
      } catch {
        // Silently fail
      } finally {
        setUsersLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const emitFilters = React.useCallback(
    (newFilters: FilterState) => {
      const params: Record<string, string> = {};

      if (newFilters.actionType && newFilters.actionType !== "all") {
        params.action_type = newFilters.actionType;
      }
      if (newFilters.tableName && newFilters.tableName !== "all") {
        params.table_name = newFilters.tableName;
      }
      if (newFilters.userIds.length > 0) {
        params.user_id = newFilters.userIds.join(",");
      }
      if (newFilters.startDate) {
        params.action_timestampFrom = format(
          newFilters.startDate,
          "yyyy-MM-dd"
        );
      }
      if (newFilters.endDate) {
        params.action_timestampTo = format(
          newFilters.endDate,
          "yyyy-MM-dd'T'23:59:59"
        );
      }

      onFilterChange(params);
    },
    [onFilterChange]
  );

  const handleChange = (key: keyof FilterState, value: unknown) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    emitFilters(newFilters);
  };

  const toggleUser = (userId: string) => {
    const next = filters.userIds.includes(userId)
      ? filters.userIds.filter((id) => id !== userId)
      : [...filters.userIds, userId];
    handleChange("userIds", next);
  };

  const handleClearFilters = () => {
    const cleared: FilterState = {
      actionType: "all",
      tableName: "all",
      userIds: [],
    };
    setFilters(cleared);
    emitFilters(cleared);
  };

  const hasActiveFilters =
    filters.actionType !== "all" ||
    filters.tableName !== "all" ||
    filters.userIds.length > 0 ||
    filters.startDate ||
    filters.endDate;

  const userLabel =
    filters.userIds.length === 0
      ? "Users"
      : filters.userIds.length === 1
        ? (users.find((u) => u.id === filters.userIds[0])?.full_name ??
          users.find((u) => u.id === filters.userIds[0])?.initials ??
          "1 user")
        : `${filters.userIds.length} users`;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* Date range */}
      <DateRangeFilter
        value={{ from: filters.startDate, to: filters.endDate }}
        onChange={({ from, to }) => {
          const newFilters = { ...filters, startDate: from, endDate: to };
          setFilters(newFilters);
          emitFilters(newFilters);
        }}
      />

      {/* Action type */}
      <Select
        value={filters.actionType}
        onValueChange={(value) => handleChange("actionType", value)}
      >
        <SelectTrigger className="h-9 w-[180px] text-sm font-normal">
          <SelectValue placeholder="All Actions" />
        </SelectTrigger>
        <SelectContent>
          {actionTypes.map((type) => (
            <SelectItem key={type.value} value={type.value}>
              {type.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Table */}
      <Select
        value={filters.tableName}
        onValueChange={(value) => handleChange("tableName", value)}
      >
        <SelectTrigger className="h-9 w-[150px] text-sm font-normal">
          <SelectValue placeholder="All Tables" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Tables</SelectItem>
          <SelectItem value="receipts">Receipts</SelectItem>
          <SelectItem value="dispenses">Dispenses</SelectItem>
          <SelectItem value="inventory">Inventory</SelectItem>
          <SelectItem value="stock_adjustments">Stock Adjustments</SelectItem>
        </SelectContent>
      </Select>

      {/* Users popover */}
      <Popover open={usersOpen} onOpenChange={setUsersOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-9 gap-1.5 font-normal",
              filters.userIds.length > 0 && "border-primary/50 text-primary"
            )}
            disabled={usersLoading}
          >
            {userLabel}
            {filters.userIds.length > 0 && (
              <Badge
                variant="secondary"
                className="h-4 px-1 text-[10px] leading-none"
              >
                {filters.userIds.length}
              </Badge>
            )}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <CommandEmpty>No users found.</CommandEmpty>
              <CommandGroup>
                {users.map((user) => (
                  <CommandItem
                    key={user.id}
                    onSelect={() => toggleUser(user.id)}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        filters.userIds.includes(user.id)
                          ? "opacity-100"
                          : "opacity-0"
                      )}
                    />
                    {user.full_name || user.initials}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Clear all */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleClearFilters}
          className="h-9 px-2 text-muted-foreground hover:text-foreground"
        >
          Clear all
          <X className="ml-1 h-3.5 w-3.5" />
        </Button>
      )}
    </div>
  );
}

// Re-export for backward compatibility
export type AuditFilters = Record<string, string>;
